import { axios, organizationIdConfig } from '../configs';
import {
  EContentSectionType,
  ESendRecipient,
  EUserProvider,
  DraftMessage,
  ScheduledReservation,
} from '../types';

export type TChannelParticipantBase = {
  id: number;
  name: string;
  uuid: string;
  avatar?: string;
  provider: EUserProvider;
};

export type TChannelUserParticipant = TChannelParticipantBase & {
  userLineId: number;
  memo?: string;
};

export type TChannelOrganizationParticipant = TChannelParticipantBase & {
  organizationMemberId: number;
};

export type TChannelParticipant =
  | TChannelUserParticipant
  | TChannelOrganizationParticipant;

export type TChannelKind = 'MALE' | 'INQUIRY';

export type TChannelResponseStatus =
  | 'NEEDS_RESPONSE'
  | 'RESPONDED'
  | 'NO_ACTION_NEEDED';

export type TChannelBase = {
  responseStatus?: TChannelResponseStatus | null;
  id: string;
  kind: TChannelKind;
  lastMessageTime?: string;
  createdAt: string;
};

export type TChannel = TChannelBase & {
  participants: TChannelParticipant[];
  managementParticipantUuids?: string[];
  unreadCount?: number;
};

export type TGetChannelsResponse = {
  data: TChannel[];
  total: number;
  currentPage: number;
  totalPage: number;
};

export type TGetChannelsParams = {
  responseStatus?: TChannelResponseStatus;
  currentPage: number;
  pageSize: number;
  search?: string;
};

type TCreateTextMessage = {
  id: string;
  type: EContentSectionType.TEXT;
  content: string;
};

export type TMedia = {
  fileName: string;
  type: string;
  url: string;
  originalHeight: number;
  originalWidth: number;
  poster?: string;
  actionUrl?: string;
  actionLabel?: string;
};

type TCreateMediaMessage = {
  id: string;
  type: EContentSectionType.IMAGE | EContentSectionType.VIDEO;
  media: TMedia;
};

type TCreateFileMessage = {
  id: string;
  type: EContentSectionType.FILE;
  content?: string;
  media: TMedia;
};

type TCreateAssetMessage = {
  id: string;
  type:
    | EContentSectionType.RICH_MESSAGE
    | EContentSectionType.CARD_TYPE_MESSAGE;
  assetId: number;
};

type TCreateSurveyMessage = {
  id: string;
  type: EContentSectionType.SURVEY;
  surveyId: number;
  content?: string;
  imageUrl?: string;
  imageAspectRatio?: '1:1' | '16:9';
};

export type TCreateMessageForAll =
  | TCreateTextMessage
  | TCreateMediaMessage
  | TCreateFileMessage
  | TCreateAssetMessage
  | TCreateSurveyMessage;

/** Send option shape for API (send message / draft) */
export type TSendOptionPayload = {
  recipient?: string;
  sendTime?: string;
  scheduledDateTime?: string;
  tagIds?: number[];
};

/**
 * Get number of recipients for send message for all (配信数)
 * 配信対象（全員／タグ絞り込み／タグなし）は送信時と同じ recipient で指定する。
 * recipient=filter のときだけタグIDを渡す。
 */
export const getDeliveryCount = async (params: {
  recipient: ESendRecipient;
  tagIds?: number[];
}): Promise<{ count: number }> => {
  const res = await axios
    .get<{ count: number }>('messages/delivery-count', {
      params: {
        recipient: params.recipient,
        ...(params.tagIds && params.tagIds.length > 0
          ? { tagIds: params.tagIds.join(',') }
          : {}),
      },
    })
    .then((response) => response.data);
  return res;
};

/**
 * Get number of test delivery targets (テスト配信対象数)
 */
export const getTestDeliveryCount = async (): Promise<{ count: number }> => {
  const res = await axios
    .get<{ count: number }>('messages/test-delivery-count')
    .then((response) => response.data);
  return res;
};

/**
 * Get list of channels for management app
 */
export const getManagementChannels = async (
  params: TGetChannelsParams,
): Promise<TGetChannelsResponse> => {
  const res = await axios
    .get<TGetChannelsResponse>('messages/channels/management', {
      params,
    })
    .then((response) => response.data);

  return res;
};

/**
 * Get channel detail by ID (management / userLine 共通DTO想定)
 */
export const getChannelDetail = async (
  channelId: string,
): Promise<TChannel> => {
  const res = await axios.get<TChannel>(
    `messages/channel/management/${channelId}`,
  );
  return res.data;
};

export const updateChannelResponseStatus = async (
  channelId: string,
  responseStatus: TChannelResponseStatus,
  organizationId?: number | null,
): Promise<{ responseStatus: TChannelResponseStatus }> => {
  const res = await axios.patch<{ responseStatus: TChannelResponseStatus }>(
    `messages/channel/${channelId}/response-status`,
    { responseStatus },
    organizationIdConfig(organizationId),
  );
  return res.data;
};

/**
 * Get total unread messages count across all channels for management.
 * 数えるのは利用者(LINEユーザー)からの未読のみで、管理側の送信は含まれない。
 * backend が1クエリで集計するため、チャンネル一覧の取得は不要。
 */
export const getTotalUnreadCount = async (): Promise<number> => {
  const res = await axios.get<{ total: number }>('messages/unread-count');
  return res.data?.total ?? 0;
};

/**
 * Update seen time for a channel
 */
export const updateSeenTime = async (
  channelId: string,
  organizationId?: number | null,
): Promise<boolean> => {
  const res = await axios.patch<boolean>(
    `messages/channel/${channelId}/seen`,
    undefined,
    organizationIdConfig(organizationId),
  );
  return res.data;
};

/**
 * Notify LINE: send a message to male participants with a button to open the male app chat.
 * Call after management sends a message in the chat.
 */
/**
 * 通知APIの再試行可否を判定する。
 * 応答が無い（ネットワーク断・タイムアウト）か5xxのときだけ再送する。
 * @param error axiosが返したエラー
 * @returns 再試行してよいならtrue
 */
const isRetriableNotifyError = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number } } | null)
    ?.response;
  // 応答が無い（ネットワーク断・タイムアウト）は送信到達が不明なので再試行する
  if (!response) return true;

  return (response.status ?? 0) >= 500;
};

export const notifyLine = async (
  channelId: string,
  // timetokenが取得できなかった場合はundefinedのまま送り、backendの
  // サーバ側解決（操作者自身の未通知の最新メッセージ特定）に委ねる
  messageTimetoken?: string,
  // 再試行の待ち時間中に組織が変わっても送信時の組織へ通知する
  organizationId?: number | null,
): Promise<void> => {
  const retryDelaysMs = [0, 250, 1000] as const;
  let lastError: unknown;

  for (const delayMs of retryDelaysMs) {
    if (delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    try {
      await axios.post(
        `messages/channel/${channelId}/notify-line`,
        { messageTimetoken },
        organizationIdConfig(organizationId),
      );
      return;
    } catch (error) {
      lastError = error;
      // 4xxは再試行しても結果が変わらない。加えて、backendが
      // messageTimetokenで冪等化していない旧版だと、無条件の再試行は
      // 同じ通知を繰り返し届けてしまう。再送は一過性の失敗に限定する。
      if (!isRetriableNotifyError(error)) break;
    }
  }

  throw lastError;
};

/**
 * Update channel active time (when sending a message)
 * 遅延実行されるため、呼び出し時点の組織を organizationId で固定できる。
 */
export const updateChannelActive = async (
  channelId: string,
  organizationId?: number | null,
): Promise<boolean> => {
  await Promise.all([
    axios.patch(
      `messages/channel/${channelId}/sent`,
      undefined,
      organizationIdConfig(organizationId),
    ),
    updateSeenTime(channelId, organizationId),
  ]).then(() => true);

  return true;
};

/** 一斉送信。アップロード等の await の後に呼ばれるため、操作開始時の組織を organizationId で固定する。 */
export const sendMessageForAll = async (
  body: {
    sections: TCreateMessageForAll[];
    sendOption?: TSendOptionPayload;
    currentDraftId?: string;
    clientMessageId: string;
  },
  organizationId?: number | null,
) => {
  return await axios
    .post(
      'messages/male',
      {
        sections: body.sections,
        sendOption: body.sendOption,
        currentDraftId: body.currentDraftId,
        clientMessageId: body.clientMessageId,
      },
      organizationIdConfig(organizationId),
    )
    .then(function (response) {
      return response.data;
    });
};

/**
 * Send test message to test users only with a client-generated retry identity.
 */
export const sendTestMessageForAll = async (
  body: {
    sections: TCreateMessageForAll[];
    clientMessageId: string;
  },
  organizationId?: number | null,
) => {
  return await axios
    .post(
      'messages/male/test',
      {
        sections: body.sections,
        clientMessageId: body.clientMessageId,
      },
      organizationIdConfig(organizationId),
    )
    .then(function (response) {
      return response.data;
    });
};

export type TLineDispatchSourceType = 'BULK' | 'TEST';

export type TLineDispatchAggregateStatus =
  | 'PLANNED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'PARTIALLY_FAILED'
  | 'UNKNOWN'
  | 'FAILED'
  | 'CANCELLED';

export type TLineDispatchStatus = {
  dispatch: {
    id: string;
    organizationId: number;
    sourceType: string;
    sourceRevision: number;
    intentHash: string;
    /**
     * この操作を開始したのが自分か。intentHashは本文と送信条件だけのhashで
     * 操作主体を含まないため、同じ内容を送る別管理者のoperationと区別できない。
     */
    startedByCurrentMember: boolean;
    status: TLineDispatchAggregateStatus;
    sealedAt: string | null;
    startedAt: string | null;
    firstAttemptedAt: string | null;
    completedAt: string | null;
    cancelledAt: string | null;
  };
  summary: {
    status: TLineDispatchAggregateStatus;
    sentRecipientCount: number;
    unavailableRecipientCount: number;
    unknownRecipientCount: number;
  };
};

/**
 * 保存したsource identityに対応する未完了dispatchを取得する。
 * reloadや別tabからの再試行でも、新しいUUIDを発行せず同じproducer処理を再開するために使う。
 */
export const getActiveLineDispatch = async (params: {
  sourceType: TLineDispatchSourceType;
  sourceKey?: string;
  sourceRevision?: number;
}): Promise<TLineDispatchStatus[]> => {
  return axios
    .get<TLineDispatchStatus[]>('line-dispatches/active', { params })
    .then((response) => response.data);
};

/** terminalを含め、保存済みsource identityと完全一致するdispatchを取得する。 */
export const getLineDispatchBySource = async (
  params: {
    sourceType: TLineDispatchSourceType;
    sourceKey: string;
    sourceRevision: number;
  },
  organizationId?: number | null,
): Promise<TLineDispatchStatus | null> => {
  return axios
    .get<TLineDispatchStatus | null>('line-dispatches/by-source', {
      params,
      ...organizationIdConfig(organizationId),
    })
    .then((response) => response.data);
};

/** local identity消失時のresponse-loss回復用に、active＋直近terminalを取得する。 */
export const getRecentLineDispatches = async (
  params: {
    sourceType: TLineDispatchSourceType;
  },
  organizationId?: number | null,
): Promise<TLineDispatchStatus[]> => {
  return axios
    .get<TLineDispatchStatus[]>('line-dispatches/recent', {
      params,
      ...organizationIdConfig(organizationId),
    })
    .then((response) => response.data);
};

/** 保存済みjournalだけを正本に、既存dispatchの安全なrequestを再開する。 */
export const retryLineDispatch = async (
  dispatchId: string,
  organizationId?: number | null,
): Promise<TLineDispatchStatus & { processedRequestCount: number }> => {
  return axios
    .post<TLineDispatchStatus & { processedRequestCount: number }>(
      `line-dispatches/${dispatchId}/retry`,
      undefined,
      organizationIdConfig(organizationId),
    )
    .then((response) => response.data);
};

/** provider未試行のdispatchだけを破棄する。backendが開始済みなら409で拒否する。 */
export const cancelLineDispatch = async (
  dispatchId: string,
  organizationId?: number | null,
): Promise<TLineDispatchStatus['dispatch']> => {
  return axios
    .post<TLineDispatchStatus['dispatch']>(
      `line-dispatches/${dispatchId}/cancel`,
      undefined,
      organizationIdConfig(organizationId),
    )
    .then((response) => response.data);
};

/**
 * Save draft message (same shape as send: sections + sendOption)
 */
export const saveDraftMessage = async (
  draft: {
    sections: TCreateMessageForAll[];
    sendOption?: TSendOptionPayload;
  },
  organizationId?: number | null,
): Promise<DraftMessage> => {
  const res = await axios
    .post<DraftMessage>(
      'messages/drafts',
      draft,
      organizationIdConfig(organizationId),
    )
    .then((response) => response.data);
  return res;
};

/**
 * Update draft message (partial: sections and/or sendOption)
 */
export const updateDraftMessage = async (
  id: string,
  draft: Partial<{
    sections: TCreateMessageForAll[];
    sendOption: TSendOptionPayload;
  }>,
  organizationId?: number | null,
): Promise<DraftMessage> => {
  const res = await axios
    .patch<DraftMessage>(
      `messages/drafts/${id}`,
      draft,
      organizationIdConfig(organizationId),
    )
    .then((response) => response.data);
  return res;
};

/**
 * Get paginated draft messages
 */
export const getDraftMessages = async (params?: {
  currentPage?: number;
  pageSize?: number;
}): Promise<{
  data: DraftMessage[];
  total: number;
  currentPage: number;
  totalPage: number;
}> => {
  const res = await axios
    .get<{
      data: DraftMessage[];
      total: number;
      currentPage: number;
      totalPage: number;
    }>('messages/drafts', {
      params: {
        currentPage: params?.currentPage || 1,
        pageSize: params?.pageSize || 10,
      },
    })
    .then((response) => response.data);
  return res;
};

/**
 * Get draft message by ID
 */
export const getDraftMessage = async (
  id: string,
): Promise<DraftMessage | null> => {
  try {
    const res = await axios
      .get<DraftMessage>(`messages/drafts/${id}`)
      .then((response) => response.data);
    return res;
  } catch (error) {
    console.error('Error loading draft:', error);
    return null;
  }
};

/**
 * Delete draft message
 */
export const deleteDraftMessage = async (id: string): Promise<boolean> => {
  await axios.delete(`messages/drafts/${id}`);
  return true;
};

/**
 * 未送信の配信予約を配信予定日時の昇順で取得する
 */
export const getReservations = async (params?: {
  currentPage?: number;
  pageSize?: number;
}): Promise<{
  data: ScheduledReservation[];
  total: number;
  currentPage: number;
  totalPage: number;
  truncated: boolean;
}> => {
  const res = await axios
    .get<{
      data: ScheduledReservation[];
      total: number;
      currentPage: number;
      totalPage: number;
      truncated: boolean;
    }>('messages/reservations', {
      params: {
        currentPage: params?.currentPage || 1,
        pageSize: params?.pageSize || 10,
      },
    })
    .then((response) => response.data);
  return res;
};

/**
 * 編集のために配信予約を1件取得する。
 * 取り消し済み・配信開始済みは呼び出し側で扱えるようエラーをそのまま投げる。
 */
export const getReservation = async (
  id: string,
): Promise<ScheduledReservation> => {
  const res = await axios
    .get<ScheduledReservation>(`messages/reservations/${id}`)
    .then((response) => response.data);
  return res;
};

/**
 * 配信予約の本文と配信条件を更新する
 */
export const updateReservation = async (
  id: string,
  reservation: {
    sections: TCreateMessageForAll[];
    sendOption: TSendOptionPayload;
  },
  organizationId?: number | null,
): Promise<ScheduledReservation> => {
  const res = await axios
    .patch<ScheduledReservation>(
      `messages/reservations/${id}`,
      reservation,
      organizationIdConfig(organizationId),
    )
    .then((response) => response.data);
  return res;
};

/**
 * 配信予約を取り消す
 */
export const cancelReservation = async (id: string): Promise<boolean> => {
  await axios.delete(`messages/reservations/${id}`);
  return true;
};

/**
 * Delete a message in individual chat (PubNub)
 */
export const deleteChannelMessage = async (
  channelId: string,
  timetoken: string,
): Promise<{ success: boolean }> => {
  const res = await axios
    .delete<{ success: boolean }>(
      `messages/channel/management/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(timetoken)}`,
    )
    .then((response) => response.data);
  return res;
};

export type TRecallStatusItem = { channelId: string; timetoken: string };

/**
 * Given each channel and its newest message timetoken, returns which of those
 * newest messages are recalled. Used by the channel list to flag the recalled
 * label after reload (the bulk PubNub history fetch cannot include message
 * actions). Shared by male and inquiry chat lists.
 */
export const getChannelsRecallStatus = async (
  items: TRecallStatusItem[],
): Promise<{ recalled: TRecallStatusItem[] }> => {
  const res = await axios
    .post<{ recalled: TRecallStatusItem[] }>(
      'messages/channels/recall-status',
      { items },
    )
    .then((response) => response.data);
  return res;
};
